import {Button, Card, CardContent, Grid, useTheme} from "@mui/material";
import React, {MouseEventHandler, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {unitsEnumAtom} from "../../../state/rentalPeriodsRecoil";
import {ancillaryTypeAtom, chargingWayAtom, currencyAtom,} from "../../../state/constantsRecoil";
import {AncillaryEntityResponse, FormAncillaryModel,} from "../../../api/types/ancillary";
import CustomBanner from "../../../common/components/CustomBanner";
import {useFormContext} from "react-hook-form";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {SelectElementModelFE} from "../../../api/types/generic";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {ancillaryAtom} from "../../../state/ancillariesRecoil";
import dayjs from "dayjs";
import {useConstantsRepository} from "../../../api/repositories/ConstantsRepository";
import {i18nConvertNumberWithDecimals, setFormNumberValue} from "../../../utils";
import {numberIntegerRegex, numberRegex} from "../../../constants";
import {useAncillaryRepository} from "../../../api/repositories/AncillaryRepository";
import DealerAndTenant from "../../../common/components/DealerAndTenant";

const AncillaryBaseDefinition = ({
                                     readOnly,
                                     handleSubmit,
                                     ancillaryInUse,
                                 }: {
    readOnly: boolean;
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
    ancillaryInUse: boolean;
}) => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Recoil
    const [commericalSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [unitsEnum] = useRecoilState(unitsEnumAtom);
    const [currencyList] = useRecoilState(currencyAtom);
    const [chargingWayList] = useRecoilState(chargingWayAtom);
    const [types] = useRecoilState(ancillaryTypeAtom);
    const [ancillary] = useRecoilState<AncillaryEntityResponse | null>(ancillaryAtom);

    //Use State
    const [subtypes, setSubtypes] = useState<string[]>([]);
    const [serviceItem, setServiceItem] = useState<any>([]);

    // Use Form Context
    const {formState, register, setValue, watch, getValues} =
        useFormContext<FormAncillaryModel>();
    const watchAllFields = watch();

    // Use Repository Hooks
    const {getSubtypes} = useConstantsRepository();
    const {getServiceItem} = useAncillaryRepository();

    // Use Form Context
    const form = useFormContext<FormAncillaryModel>();

    useEffect(() => {
        const getContent = async () => {
            if (ancillary?.id) {
                const subtypes: string[] = await getSubtypes(ancillary.type);
                setSubtypes(subtypes);
            }
            // @ts-ignore
            const services = await getServiceItem(form.getValues("legalEntity")?.code, form.getValues("dealer")?.code);
            setServiceItem(services);
        };
        if (form.getValues("legalEntity") && form.getValues("dealer")) {
            getContent();
        }
    }, [form.getValues("legalEntity"), form.getValues("dealer"), ancillary]);

    const handleChangeType = async (type: string) => {
        setValue("type", type);
        setValue("subtype", "");
        //setValue("exceptionListPack", null)
        if (watch("type")) {
            const subtypes: string[] = await getSubtypes(type);
            setSubtypes(subtypes);
        }
    };

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && ancillaryInUse &&
                <CustomBanner message={t("pricing:ancillary.banner")} severity={"warning"}/>}
            <Grid item xs={12} sm={12} md={12}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} py={2}>
                            <DealerAndTenant
                                readOnly={readOnly}
                                md={3}
                                legalEntityName={"legalEntity"}
                                dealerName={"dealer"}
                                dealerIsVisible={true}
                                idForReadOnly={form.getValues("id")}
                            />
                            {/* COMMERCIAL SEGMENT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("segments")}
                                    label={`${t("pricing:ancillary.form.labels.commercialSegment")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={commericalSegmentList || []}
                                    multiple={true}
                                    isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                    //handleChange={(value: SelectElementModelFE[]) => getExceptionListToPack(value)}
                                />
                            </Grid>
                            {/* CODE */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("code")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.code")}*`}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* NAME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("nameAncillary")}
                                    label={`${t("pricing:ancillary.form.labels.nameAncillary")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* DESCRIPTION */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("description")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.description")}*`}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* CARDINALITY */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("cardinality")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.cardinality")}*`}
                                    readOnly={readOnly}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "cardinality", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* ADDITIONAL FIELD */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("additionalField")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.additionalFieldType")}
                                    readOnly={readOnly}
                                    options={["NUMBER", "STRING"]}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                />
                            </Grid>
                            {/* UNIT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("tpUnit")}
                                    label={`${t("pricing:ancillary.form.labels.unit")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={unitsEnum}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                />
                            </Grid>
                            {/* TAX */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("tax")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.tax")}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* RESERVATION FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationFrom")}
                                    label={`${t("pricing:ancillary.form.labels.reservationFrom")}*`}
                                    readOnly={readOnly || formState.isSubmitting || ancillaryInUse}
                                    minDate={dayjs().diff(dayjs(ancillary?.reservationFrom), "minutes") > 0 ? ancillary?.reservationFrom : undefined}
                                />
                            </Grid>
                            {/* RESERVATION TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationTo")}
                                    label={`${t("pricing:ancillary.form.labels.reservationTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.reservationFrom || undefined}
                                />
                            </Grid>
                            {/* PICKUP DATE FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupFrom")}
                                    label={`${t("pricing:ancillary.form.labels.pickupDateFrom")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={dayjs().diff(dayjs(ancillary?.pickupFrom), "minutes") > 0 ? ancillary?.pickupFrom : undefined}
                                />
                            </Grid>
                            {/* PICKUP DATE TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupTo")}
                                    label={`${t("pricing:ancillary.form.labels.pickupDateTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.pickupFrom || undefined}
                                />
                            </Grid>
                            {/* CURRENCY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("currency")}
                                    label={`${t("pricing:ancillary.form.labels.currency")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={currencyList}
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            {/*<Grid item xs={ 12 } md={ 3 }>*/}
                            {/*    <FormAutocomplete*/}
                            {/*        { ...register("dealer") }*/}
                            {/*        label={ `${ t("pricing:ancillary.form.labels.tenant") }*` }*/}
                            {/*        readOnly={ readOnly || formState.isSubmitting }*/}
                            {/*        options={ providers }*/}
                            {/*        isOptionEqualToValue={ (option, value) => option.code === value.code }*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            {/* MINIMUM AMOUNT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("minimumAmount")}
                                    label={`${t("pricing:ancillary.form.labels.minimumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "minimumAmount", numberRegex);
                                    }}
                                />
                            </Grid>
                            {/* MAXIMUM AMOUNT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("maximumAmount")}
                                    label={`${t("pricing:ancillary.form.labels.maximumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "maximumAmount", numberRegex);
                                    }}
                                />
                            </Grid>
                            {/* CHARGING WAY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargingWay")}
                                    label={`${t("pricing:ancillary.form.labels.chargingWay")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={chargingWayList}
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            {/* TIME UNITS MAX */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("maxUnits")}
                                    label={`${t("pricing:ancillary.form.labels.maxUnits")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "maxUnits", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* CHARGE TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargeType")}
                                    label={`${t("pricing:ancillary.form.labels.chargeType")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={["VALUE", "PERCENTAGE"]}
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            {/* TAXCODE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("taxCode")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.taxCode")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={serviceItem.map((item : any) => ({
                                        label: item.name,
                                        value: item.code,
                                    }))}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                            </Grid>
                            {/* SELL DESK */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("sellDesk")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.sellDesk")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("type")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.type")}*`}
                                    options={types}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                    readOnly={readOnly}
                                    handleChange={handleChangeType}
                                />
                            </Grid>
                            {/*/!* PACK *!/*/}
                            {/*{getValues("type") === "Pack" &&*/}
                            {/*    <Grid item xs={12} md={3}>*/}
                            {/*        <FormAutocomplete*/}
                            {/*            {...register("exceptionListPack")}*/}
                            {/*            label={`${t("pricing:ancillaryLevelOne.form.labels.exceptionList")}*`}*/}
                            {/*            options={ancillaryPack}*/}
                            {/*            isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}*/}
                            {/*            readOnly={readOnly || getValues("segments").length === 0}*/}
                            {/*            handleChange={(value: SelectElementModelFE) => handleChangePack(value)}*/}
                            {/*        />*/}
                            {/*    </Grid>}*/}
                            {/* SUBTYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("subtype")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.subtype")}*`}
                                    options={subtypes}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                    readOnly={readOnly}
                                />
                            </Grid>
                            {/* APPLICATION TIMING */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("applicationTiming")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.applicationTiming")}
                                    readOnly={readOnly}
                                    options={["PICK UP", "DROP OFF"]}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                />
                            </Grid>
                            {/* TOLERANCE TIME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("toleranceTime")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.toleranceTime")}`}
                                    readOnly={readOnly}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "toleranceTime", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* PASSAGE TO COUNTER*/}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("passageToCounterNeeded")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.passageToCounterNeeded")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* RF-02 (US-SYS-004) - 5 flag ancillary */}
                            {/* FLAG RESELLABLE AT DESK POST RA */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("flagResellableAtDeskPostRa")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.flagResellableAtDeskPostRa")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* FLAG RESELLABLE IN SUBSTITUTION */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("flagResellableInSubstitution")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.flagResellableInSubstitution")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* FLAG DISCOUNT APPLICABLE */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("flagDiscountApplicable")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.flagDiscountApplicable")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* TIME TO CHANGE (intero nullable: null = nessun limite, 0 = disabilita) */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("timeToChange")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.timeToChange")}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "timeToChange", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* TIME TO REMOVE (intero nullable: null = nessun limite, 0 = disabilita) */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("timeToRemove")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.timeToRemove")}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "timeToRemove", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* NOTE */}
                            <Grid item xs={12} md={12}>
                                <FormTextField
                                    {...register("note")}
                                    label={`${t("pricing:ancillary.form.labels.notes")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {!readOnly && (
                <Grid
                    container
                    item
                    xs={12}
                    justifyContent="flex-end"
                    columnGap={2}
                >
                    <Grid>
                        <Button
                            variant="outlined"
                            sx={{marginRight: "15px"}}
                            color={"accent"}
                            type={"button"}
                            disabled={formState.isSubmitting}
                            onClick={() => {
                                window.close()
                            }}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button
                            color={"accent"}
                            type={"button"}
                            onClick={handleSubmit}
                            sx={{
                                border: `1px solid ${theme.palette.accent.main}`
                            }}
                            disabled={formState.isSubmitting}
                        >
                            {t("pricing:actions.proceed")}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default AncillaryBaseDefinition;
